import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'

import { ConfusionList } from '@/components/benchmark/ConfusionList'
import { OutcomeBar } from '@/components/benchmark/OutcomeBar'
import { OutcomeBadge } from '@/components/ui/Badge'
import { Button, Pagination, Select } from '@/components/ui/Controls'
import { PageHeader, Panel } from '@/components/ui/Panel'
import { EmptyState, QueryBoundary, Skeleton, TableSkeleton } from '@/components/ui/States'
import {
  DEFAULT_BENCHMARK_FILTERS,
  useBenchmarkResults,
  useBenchmarkRun,
} from '@/features/benchmark/useBenchmark'
import { formatMs, formatPercent } from '@/lib/utils/format'
import type { BenchmarkFilters, BenchmarkResultRow } from '@/types/domain'

const OUTCOME_OPTIONS = [
  { value: '', label: 'all outcomes' },
  { value: 'CORRECT_TOOL', label: 'correct' },
  { value: 'WRONG_TOOL', label: 'wrong tool' },
  { value: 'TOOL_FAILED', label: 'tool failed' },
  { value: 'NO_TOOL_SELECTED', label: 'no tool' },
]

const DIFFICULTY_OPTIONS = [
  { value: '', label: 'any' },
  { value: 'easy', label: 'easy' },
  { value: 'medium', label: 'medium' },
  { value: 'hard', label: 'hard' },
]

function ResultDetail({ result }: { result: BenchmarkResultRow }) {
  return (
    <tr className="bg-raised/60">
      <td colSpan={7} className="px-3 py-3">
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <dt className="text-xs text-ink-muted">Query</dt>
            <dd className="text-sm text-ink">{result.user_query}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">Expected</dt>
            <dd className="mono text-ink-dim">{result.expected_tool}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">Selected</dt>
            <dd className="mono text-ink-dim">{result.selected_tool ?? 'none'}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">Selection latency</dt>
            <dd className="mono text-ink-dim">{formatMs(result.selection_latency_ms)}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">Execution latency</dt>
            <dd className="mono text-ink-dim">{formatMs(result.execution_latency_ms)}</dd>
          </div>
          {result.error_type ? (
            <div>
              <dt className="text-xs text-ink-muted">Error</dt>
              <dd className="mono text-fail">{result.error_type}</dd>
            </div>
          ) : null}
          {result.notes ? (
            <div className="sm:col-span-2">
              <dt className="text-xs text-ink-muted">Notes</dt>
              <dd className="text-sm text-ink-muted">{result.notes}</dd>
            </div>
          ) : null}
        </dl>
      </td>
    </tr>
  )
}

export function BenchmarkRunPage() {
  const { runUid } = useParams({ from: '/benchmark/$runUid' })
  const [filters, setFilters] = useState<BenchmarkFilters>(DEFAULT_BENCHMARK_FILTERS)
  const [expanded, setExpanded] = useState<string | null>(null)

  const run = useBenchmarkRun(runUid)
  const results = useBenchmarkResults(runUid, filters)
  const meta = run.data?.run
  const rows = results.data?.items ?? []
  const filtered = Boolean(filters.outcome || filters.difficulty)

  return (
    <div>
      <PageHeader
        title="Benchmark run"
        description={
          meta
            ? `${meta.mode}${meta.model ? ` · ${meta.model}` : ''} · dataset v${meta.dataset_version ?? '?'} · GitHub ${meta.github_mode}`
            : undefined
        }
        actions={
          <Link to="/benchmark" className="text-xs text-primary underline-offset-2 hover:underline">
            All runs
          </Link>
        }
      />

      <QueryBoundary
        isLoading={run.isLoading}
        error={run.error}
        onRetry={() => void run.refetch()}
        loadingFallback={<Skeleton className="h-32 w-full" />}
      >
        {meta ? (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            <Panel
              title="Outcomes"
              description={`${formatPercent(meta.tool_selection_accuracy, 1)} tool-selection accuracy across ${meta.total_queries} queries`}
            >
              <OutcomeBar
                correct={meta.correct_tool}
                wrongTool={meta.wrong_tool}
                toolFailed={meta.tool_failed}
                noTool={meta.no_tool_selected}
                total={meta.total_queries}
              />
            </Panel>

            <Panel
              title="Confused tools"
              description="Each pair is two descriptions the model could not tell apart"
            >
              <ConfusionList pairs={run.data?.confusion ?? []} />
            </Panel>
          </div>
        ) : null}
      </QueryBoundary>

      <Panel title="Queries" className="mt-4">
        <div className="flex flex-wrap items-center gap-4 border-b border-line px-4 py-2.5">
          <Select
            label="outcome"
            options={OUTCOME_OPTIONS}
            value={filters.outcome ?? ''}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                outcome: (event.target.value || undefined) as BenchmarkFilters['outcome'],
                offset: 0,
              }))
            }
          />
          <Select
            label="difficulty"
            options={DIFFICULTY_OPTIONS}
            value={filters.difficulty ?? ''}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                difficulty: event.target.value || undefined,
                offset: 0,
              }))
            }
          />
          {filtered ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters(DEFAULT_BENCHMARK_FILTERS)}
            >
              Clear filters
            </Button>
          ) : null}
        </div>

        <QueryBoundary
          isLoading={results.isLoading}
          error={results.error}
          onRetry={() => void results.refetch()}
          isEmpty={rows.length === 0}
          loadingFallback={<TableSkeleton rows={8} columns={6} />}
          emptyState={
            <EmptyState
              title="No queries match these filters."
              hint="Clear the filters to see every query in the run."
            />
          }
        >
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col" className="w-16">
                    ID
                  </th>
                  <th scope="col">Query</th>
                  <th scope="col" className="w-56">
                    Expected
                  </th>
                  <th scope="col" className="w-56">
                    Selected
                  </th>
                  <th scope="col" className="w-32">
                    Result
                  </th>
                  <th scope="col" className="w-24">
                    Difficulty
                  </th>
                  <th scope="col" className="w-24 text-right">
                    Latency
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((result) => {
                  const isOpen = expanded === result.query_id
                  const mismatch = result.outcome === 'WRONG_TOOL'
                  return (
                    <>
                      <tr
                        key={result.query_id}
                        data-clickable="true"
                        onClick={() => setExpanded(isOpen ? null : result.query_id)}
                      >
                        <td className="mono text-ink-muted">{result.query_id}</td>
                        <td className="max-w-md truncate text-ink-dim">{result.user_query}</td>
                        <td className="mono truncate text-ink-muted">{result.expected_tool}</td>
                        <td className="mono truncate">
                          <span className={mismatch ? 'text-miss' : 'text-ink-muted'}>
                            {result.selected_tool ?? '—'}
                          </span>
                        </td>
                        <td>
                          <OutcomeBadge outcome={result.outcome} />
                        </td>
                        <td className="text-xs text-ink-muted">{result.difficulty ?? '—'}</td>
                        <td className="mono text-right text-ink-muted">
                          {formatMs(result.total_latency_ms)}
                        </td>
                      </tr>
                      {isOpen ? <ResultDetail key={`${result.query_id}-detail`} result={result} /> : null}
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            total={results.data?.total ?? 0}
            limit={filters.limit}
            offset={filters.offset}
            onChange={(offset) => setFilters((current) => ({ ...current, offset }))}
          />
        </QueryBoundary>
      </Panel>
    </div>
  )
}
